<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="backButtonRoute ? true : false"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
    :tabs="getTabs"
    :selectedIndex="getSelectedIndex"
    ref="gc-base-layout"
    class="touch-sales-order-layout stock-take-count"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Stocktake'"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="'Stocktake #' + baseView.id"
        :secondaryLabel="undefined"
      />
    </template>

    <div class="css-v2 transaction-screen" v-if="loaded">
      <div class="gc-header-form">
        <div class="view-card responsive-paddings">
          <gc-form
            :formOptions="{
              gcLabelLocation: 'left',
            }"
            :formItems="getHeaderFormItems"
            :isNormal="true"
            :data-model="loaded ? model : {}"
            :formMode="baseView.mode"
            :fromFrom="'gc-header-form'"
            ref="gc-header-form"
            :checkValid="isValid"
            :validationGroup="'gc-header-form'"
            :scrollingEnabled="false"
            :formContentReady="onHeaderFormContentReady"
            :formHeight="40"
          ></gc-form>

          <div>
            <gc-status-chip :status="model.Status ? model.Status : ''" />
            <DxButton
              class="apply-btn"
              :text="'Cancel'"
              type="normal"
              @click="cancelStockTake"
              :visible="isEditable"
              :disabled="!getStocktakeManagementAccess"
            />
            <DxButton
              class="apply-btn"
              :text="'Commit'"
              type="default"
              @click="commitStockTake"
              :visible="isEditable"
              :disabled="!getStocktakeManagementAccess"
            />
          </div>
        </div>
      </div>

      <div class="gc-details-grid">
        <gc-filter-split-view
          :showLeftContainer="showLeftContainer"
          :showRightContainer="showRightContainer"
          :rightContainerWidth="250"
        >
          <template #leftContent>
            <dx-data-grid
              :height="getGridHeight"
              :columns="getColumns"
              :data-source="getDataSource"
              @editor-preparing="onEditorPreparing"
              :on-editing-start="onEditingStart"
              ref="dx-data-grid-comp"
              :key-expr="'ID'"
              :repaint-changes-only="true"
              :show-borders="true"
              :two-way-binding-enabled="false"
              :focusedRowEnabled="true"
              @saved="onSaved"
              @focused-row-changed="onFocusedRowChanged"
              @row-updated="rowUpdated"
              :focused-row-index="focusedRowIndex"
              :column-chooser="{ enabled: false }"
              :pager="{ visible: dataGridViewType === 'product-list' }"
              @toolbar-preparing="onToolbarPreparing($event)"
            >
              <DxScrolling
                :mode="'virtual'"
                :row-rendering-mode="'virtual'"
                :preloadEnabled="true"
              />
              <DxEditing
                :allow-adding="false"
                :allow-updating="baseView.mode !== 'review'"
                :allow-deleting="false"
                mode="cell"
                :select-text-on-edit-start="true"
                :changes="changes"
              />
              <template #Decimal="{ data }">
                <GcDecimal :dataValue="data" :format="data.column.format" />
              </template>

              <template #DecimalCell="{ data }">
                <GcDecimalCell :dataValue="data" :format="data.column.format" />
              </template>

              <template #batch-no-edit-template="{ data }">
                <div id="batch-root">
                  <div v-if="data.data.IsBatchProduct != 'No'">
                    <DxSelectBox
                      :data-source="getBatchNoDataSource(data.data.ProductCode)"
                      keyExpr="RecordId"
                      displayExpr="BatchNo"
                      :drop-down-options="{ width: 350 }"
                      class="gc-dropdown-icon-button gc-batch-selection"
                      styling-mode="text"
                      :show-arrow-icon="false"
                      :value="data.value"
                      valueExpr="RecordId"
                      :acceptCustomValue="true"
                      item-template="batch-no-item-template"
                      :search-enabled="true"
                      :searchExpr="['BatchNo', 'SupplierName']"
                      :on-item-click="
                        (e) => {
                          onBatchNoSelectionChanged(e, data.data);
                        }
                      "
                      :on-selection-changed="
                        (e) => {
                          onBatchNoSelectionChanged(
                            {
                              itemData: e.selectedItem,
                              component: e.component,
                            },
                            data.data
                          );
                        }
                      "
                      @customItemCreating="
                        onBatchCustomItemCreating($event, data, 'BatchNo')
                      "
                    >
                      <template #batch-no-item-template="{ data }">
                        <div
                          class="custom-auto-complete batch-no-edit-template"
                        >
                          <div>
                            <span class="auto-complete-primary">{{
                              data.BatchNo
                            }}</span>
                            <span class="auto-complete-secondary">{{
                              data.SupplierCode
                            }}</span>
                            <span class="auto-complete-secondary">{{
                              data.SupplierName
                            }}</span>
                          </div>
                          <div>
                            <span> {{ displayBestOrUseDate(data).text }}</span>
                            <span class="auto-complete-secondary">
                              {{ displayBestOrUseDate(data).value }}
                            </span>
                          </div>
                        </div>
                      </template>
                    </DxSelectBox>
                  </div>
                  <div v-else>
                    <div>{{ data.data.BatchNo }}</div>
                  </div>
                </div>
              </template>
              <template #supplier-cell-template="{ data }">
                <div id="supplier-root">
                  <div v-if="data.data.IsBatchProduct != 'No'">
                    <DxSelectBox
                      :data-source="
                        getSupplierDataSource(data.data.ProductCode)
                      "
                      keyExpr="SupplierCode"
                      displayExpr="SupplierCode"
                      :drop-down-options="{ width: 300 }"
                      class="gc-dropdown-icon-button gc-batch-selection"
                      styling-mode="text"
                      :show-arrow-icon="false"
                      :value="data.value"
                      valueExpr="SupplierCode"
                      :acceptCustomValue="false"
                      item-template="supplier-item-template"
                      :search-enabled="true"
                      :on-selection-changed="
                        (e) => {
                          onSupplierCodeSelectionChanged(e, data.data);
                        }
                      "
                      :searchExpr="['SupplierCode', 'SupplierName']"
                    >
                      <template #supplier-item-template="{ data }">
                        <div class="custom-auto-complete">
                          <span class="auto-complete-primary">{{
                            data.SupplierName
                          }}</span>
                          <span class="auto-complete-secondary">{{
                            data.SupplierCode
                          }}</span>
                        </div>
                      </template>
                    </DxSelectBox>
                  </div>
                  <div v-else>
                    <div>{{ data.data.SupplierCode }}</div>
                  </div>
                </div>
              </template>

              <template #count-status-template="{ data }">
                <div class="count-status-template">
                  <div v-if="data.data.CountStatus === 'Counted'">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7 10L9 12L13 8M1 10C1 11.1819 1.23279 12.3522 1.68508 13.4442C2.13738 14.5361 2.80031 15.5282 3.63604 16.364C4.47177 17.1997 5.46392 17.8626 6.55585 18.3149C7.64778 18.7672 8.8181 19 10 19C11.1819 19 12.3522 18.7672 13.4442 18.3149C14.5361 17.8626 15.5282 17.1997 16.364 16.364C17.1997 15.5282 17.8626 14.5361 18.3149 13.4442C18.7672 12.3522 19 11.1819 19 10C19 8.8181 18.7672 7.64778 18.3149 6.55585C17.8626 5.46392 17.1997 4.47177 16.364 3.63604C15.5282 2.80031 14.5361 2.13738 13.4442 1.68508C12.3522 1.23279 11.1819 1 10 1C8.8181 1 7.64778 1.23279 6.55585 1.68508C5.46392 2.13738 4.47177 2.80031 3.63604 3.63604C2.80031 4.47177 2.13738 5.46392 1.68508 6.55585C1.23279 7.64778 1 8.8181 1 10Z"
                        stroke="#079455"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <gc-warning-icon
                    v-else-if="data.data.CountStatus === 'Pending'"
                    @clicked="clickOnBatchView(data.data)"
                  />
                </div>
              </template>
            </dx-data-grid>
          </template>

          <template #rightContent>
            <gc-filter-panel
              :toolbarVisible="true"
              :toolbar="filterToolbar"
              :formItemData="getFilterFormItems"
              :setGridPanelFilter="setGridPanelFilter"
              :gridFoolter="false"
              :showLoading="showLoading"
              :from="'transaction-screen'"
              :isNormal="false"
              :persistenceKey="undefined"
              :key="'fp-call-schedule'"
              :overWriteClear="true"
              :heightAdjustment="-20"
              :ref="'gc-filter-panel'"
            ></gc-filter-panel>
          </template>
        </gc-filter-split-view>
      </div>
    </div>

    <gcPopupStocktakeBatchCount
      v-if="visibleBatchCount"
      :popupVisible.sync="visibleBatchCount"
      :stockTakeLineData="stockTakeLineData"
      :showLoading="showLoading"
      :displayServerError="displayServerError"
      :recallFromBatch="recallFromBatch"
    />
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import DataGridMixin from "@/_mixins/data-grid.mixin";
import {
  GetIndexByTabName,
  StockTakeTabs,
  StockTakeTabsVisibility,
} from "../../../_models/tabdata/inventory/stock-take";
import { BranchService } from "../../../_services/api/sales/branch.service";
import { StocktakePrepareService } from "../../../_services/api/inventory/stocktake-prepare.service";
import { mapGetters } from "vuex";
import ColumnWidth from "../../../_helpers/data-grid/ColumnWidth";
import ArrayStore from "devextreme/data/array_store";
import { ProductService } from "../../../_services/api/products/product.service";
import { StocktakeCountService } from "../../../_services/api/inventory/stocktake-count.service";
import {
  backwardTabbing,
  forwardTabbing,
  onEnter,
} from "@/_helpers/ui/keyboard";
import { FeatureStorage } from "../../../_services/storage";
import { StockTakeListService } from "../../../_services/api/inventory/stock-take-list.service";
import SettingsService from "../../../_services/api/settings/graycorp/settings.service";
import gcPopupStocktakeBatchCount from "../../../popups/stock-take/gc-popup-stocktake-batch-count.vue";
import gcWarningIcon from "../../../components/gc-warning-icon.vue";

let itemBase = {
  BatchDateCreated: null,
  BatchNo: null,
  BinLocationCode: null,
  BinLocationLocation: null,
  BranchCode: null,
  BranchDescription: null,
  Comment: null,
  CompanyCode: null,
  Count: 0,
  CountDate: null,
  CountToDate: 0,
  HasPrimaryImage: null,
  IsBatchProduct: "No",
  ProductCategory1Code: null,
  ProductCategory2Code: null,
  ProductCategory3Code: null,
  ProductCategory4Code: null,
  ProductCode: null,
  ProductDescription: null,
  ProductSizeCode: null,
  ProductTypeCode: null,
  RecordId: null,
  ReferenceNo: null,
  StocktakeCode: null,
  SupplierCode: null,
  SupplierName: null,
  UnitOfMeasureCode: null,
  UseByDate: undefined,
  rowUpdating: false,
};

export default {
  mixins: [RouteMixin, FormMixin, DataGridMixin],
  components: { gcPopupStocktakeBatchCount, gcWarningIcon },
  data() {
    return {
      isValid: false,
      headerFormHeight: 0,
      model: {
        Branch: undefined,
      },
      loaded: false,
      countDataSource: new ArrayStore({
        key: "ID",
        data: [],
      }),
      selectedProducts: [],
      changes: [],
      onFocusIn: false,
      headerForm: undefined,
      focusedRowIndex: -1,
      Status: undefined,
      dataGridViewType: "product-entry",
      productListDataSource: new ArrayStore({
        key: "ID",
        data: [],
      }),
      showLeftContainer: true,
      showRightContainer: true,
      filterToolbar: {
        close: {
          icon: "showpanel",
          onClick: () => {
            this.closeFilterPanel();
          },
        },
      },
      StockTakeGridViews: [
        {
          label: "Product Entry",
          value: "product-entry",
        },
        {
          label: "Product List by Batch",
          value: "product-list",
        },
        {
          label: "Product List by Item",
          value: "product-list-item",
        },
      ],
      productListItemDataSource: new ArrayStore({
        key: "ID",
        data: [],
      }),
      visibleBatchCount: false,
      stockTakeLineData: undefined,
    };
  },
  async beforeMount() {
    StockTakeTabsVisibility({ ...this.baseView });
    let settingValue = await SettingsService.getBySettingName(
      "StockTakeCountViews"
    );

    if (settingValue.data.value.length > 0) {
      let SettingValue = settingValue.data.value[0].SettingValue.split(",");

      this.StockTakeGridViews.forEach((element) => {
        if (SettingValue.find((x) => x.trimStart() === element.label)) {
          element.visible = true;
        } else element.visible = false;
      });

      this.dataGridViewType = this.StockTakeGridViews.find(
        (x) => x.visible != false
      ).value;
    }
  },
  async created() {
    this.setIgnoreRouteChangeConfirmation();
    if (this.baseView.mode === "edit")
      await StocktakePrepareService.getStockTakeListItemById(this.baseView.id, [
        "Code",
        "Status",
        "FriendlyStatus",
      ])
        .then((res) => {
          let data = res.data.value[0];

          if (data) {
            this.Status = data.Status;
            this.model.Status = data.FriendlyStatus;
          }
        })
        .catch(() => {
          this.showLoading(false);
        });
    if (
      this.baseView.mode === "edit" &&
      (this.Status === "C" || this.Status === "X" || !this.getUserAccess)
    ) {
      this.$router
        .push({
          path: `/stock-take-review/review/${this.baseView.id}`,
        })
        .then(() => {
          this.loaded = true;
        });
    } else {
      this.countDataSource.clear();
      this.$nextTick(() => {
        this.countDataSource.insert(itemBase);
      });
      await StocktakePrepareService.getStocktakeBranch(this.baseView.id)
        .then(async (res) => {
          if (res.data.value.length === 1) {
            this.model.Branch = res.data.value[0].BranchCode;

            if (this.dataGridViewType !== "product-entry")
              this.showLoading(true);

            this.getStocktakeReviewList();
          }
        })
        .catch(() => {
          this.showLoading(false);
        });
      this.loaded = true;
    }

    this.$nextTick(() => {
      var item = document.getElementById("filter-btn");
      if (item) item.classList.add("dx-state-invisible");

      this.updateFilterFormItems();
    });
  },
  computed: {
    getStocktakeManagementAccess() {
      return FeatureStorage.checkPermission("StocktakeManagement", "create");
    },
    getUserAccess() {
      return (
        FeatureStorage.checkPermission("StocktakeCount", "create") ||
        FeatureStorage.checkPermission("StocktakeManagement", "create")
      );
    },
    ...mapGetters({ gridHeight: "ui/getHeightForDataGrid" }),
    getGridHeight() {
      return this.gridHeight - 20 - this.headerFormHeight;
      // 20 => Top & Bottom Padding
    },
    backButtonRoute() {
      return "/stocktake";
    },
    getTabs() {
      return StockTakeTabs();
    },
    getSelectedIndex() {
      return GetIndexByTabName("Count", { ...this.baseView });
    },
    getHeaderFormItems() {
      return [
        {
          itemType: "group",
          caption: "",
          cssClass: "header-form-items",
          colCount: 3,
          items: [
            {
              dataField: "Branch",
              editorType: "dxSelectBox",
              label: {
                text: "Branch",
              },
              editorOptions: {
                dataSource: BranchService.dataSourceConfig(
                  "api/StocktakeBranch",
                  {
                    sort: [{ selector: "BranchDescription", asc: true }],
                    filter: ["StocktakeCode", "=", parseInt(this.baseView.id)],
                  }
                ),
                valueExpr: "BranchCode",
                searchExpr: ["BranchCode", "BranchDescription"],
                searchMode: "contains",
                showClearButton: true,
                itemTemplate: (data) => {
                  return ` <div class="custom-auto-complete" id="${data.BranchCode}" >
                            <span class="auto-complete-primary">
                              ${data.BranchDescription}
                            </span>
                            <span class="record-secondary gc-grid-code">
                              ${data.BranchCode}
                            </span>
                          </div>`;
                },
                searchEnabled: true,
                displayExpr: "BranchDescription",
                acceptCustomValue: false,
                onValueChanged: this.onBranchValueChanged,
                onKeyDown: this.onBranchKeyDown,
                onFocusIn: (e) => {
                  e.event.target.select();
                },
                onItemClick: () => {
                  let item = this.countDataSource._array.find(
                    (x) => !x.ReferenceNo
                  );
                  if (item) {
                    let index = this.dataGrid.getRowIndexByKey(item.ID);
                    if (index != -1) {
                      this.focusOnEditableCell(index, "ProductCode");
                    }
                  }
                },
              },
            },
          ],
        },
      ];
    },
    getColumns() {
      if (this.dataGridViewType === "product-list-item")
        return this.getProductItemListColumns;
      else
        return [
          {
            caption: "Product",
            dataField: "ProductCode",
            width: ColumnWidth.ProductCode.width,
            cssClass: "edit-cell-textbox-field",
            editorOptions: {
              dataSource: ProductService.dataSourceConfig(
                "api/StocktakeReviewList",
                {
                  filter: [
                    ["StocktakeCode", "=", parseInt(this.baseView.id)],
                    "and",
                    ["BranchCode", "=", `${this.model.Branch}`],
                    "and",
                    ["Status", "<>", `X`],
                  ],
                  sort: "ProductCode asc",
                  select: [
                    "StocktakeCode",
                    "Status",
                    "ProductCode",
                    "ProductDescription1",
                    "BranchCode",
                    "ProductSearchText",
                    "SupplierList",
                    "ProductCategory1Code",
                    "ProductCategory2Code",
                    "ProductCategory3Code",
                    "ProductCategory4Code",
                    "ProductTypeCode",
                    "ProductSizeCode",
                    "Attribute01Code",
                    "Attribute02Code",
                    "Attribute03Code",
                    "Attribute04Code",
                    "Attribute05Code",
                    "Attribute06Code",
                    "Attribute07Code",
                    "Attribute08Code",
                    "Attribute09Code",
                    "Attribute10Code",
                    "Attribute11Code",
                    "Attribute12Code",
                    "Attribute13Code",
                    "Attribute14Code",
                    "Attribute15Code",
                    "Attribute16Code",
                    "Attribute17Code",
                    "Attribute18Code",
                    "Attribute19Code",
                    "Attribute20Code",
                  ],
                  postProcess: (data) => {
                    data.forEach((element) => {
                      this.onProductSelectionChanged(element);
                    });

                    return data;
                  },
                }
              ),
              displayExpr: "ProductCode",
              valueExpr: "ProductCode",
              searchExpr: ["ProductCode", "ProductDescription1"],
              searchTimeout: 10,
              searchEnabled: true,
              itemTemplate: (data) => {
                return ` <div class="custom-auto-complete">
                        <span class="auto-complete-primary">
                          ${data.ProductCode}
                        </span>
                        <span class="record-secondary gc-grid-code">
                          ${data.ProductDescription1}
                        </span>
                      </div>`;
              },
              dropDownOptions: {
                width: 400,
              },
              onOpened: (e) => {
                if (e) {
                  e.component.instance().reset();
                  e.component.getDataSource().reload();
                }
              },
              // onItemClick: (e) => {
              //   this.onProductSelectionChanged(e);
              // },
              onFocusIn: () => {
                this.onProductCodeFocusIn();
              },
              onKeyDown: this.onProductCodeKeyDown,
            },
            setCellValue: async (newData, value, currentRowData) => {
              if (!currentRowData.ProductCode) {
                let index = this.dataGrid.getRowIndexByKey(currentRowData.ID);
                if (index != -1) {
                  if (index === this.countDataSource._array.length - 1)
                    this.countDataSource.insert({ ...itemBase });
                }
              }

              newData.ProductCode = value;
              let prodData = this.selectedProducts.find(
                (x) => x.ProductCode === value
              );
              if (prodData) {
                this.updateLocalDataSet(currentRowData.ID, prodData);
              }

              if (newData.ProductCode)
                await StocktakeCountService.getProductDefaultData(
                  this.baseView.id,
                  newData.ProductCode,
                  currentRowData.BranchCode || this.model.Branch
                )
                  .then((res) => {
                    if (res.data.value.length != 0) {
                      let item = res.data.value[0];
                      if (item) {
                        newData.ProductDescription = item.ProductDescription;
                        newData.BranchCode = item.BranchCode;
                        newData.UnitOfMeasureCode = item.UnitOfMeasureCode;
                        newData.ReferenceNo = item.ReferenceNo;
                        newData.BinLocationCode = item.BinLocationCode;
                        // newData.BatchNo = item.BatchNo;
                        // newData.SupplierCode = item.SupplierCode;
                        newData.Count = item.Count;
                        // newData.CountToDate = item.CountToDate;
                        newData.IsBatchProduct = item.IsBatchProduct;
                      }
                    }

                    return newData;
                  })
                  .catch(() => {
                    return newData;
                  });
              else return newData;
            },
            allowEditing: this.dataGridViewType === "product-entry",
          },
          {
            caption: "Description",
            dataField: "ProductDescription",
            minWidth: ColumnWidth.ProductDescription.minWidth,
            allowEditing: false,
          },
          {
            caption: "Branch",
            dataField: "BranchCode",
            width: ColumnWidth.BranchCode.width,
            minWidth: ColumnWidth.BranchCode.minWidth,
            cssClass: "edit-cell-textbox-field",
            editorOptions: {
              dataSource: ProductService.dataSourceConfig(
                "api/StocktakeBranch",
                {
                  filter: [["StocktakeCode", "=", parseInt(this.baseView.id)]],
                  sort: "BranchDescription asc",
                }
              ),
              displayExpr: "BranchCode",
              valueExpr: "BranchCode",
              searchExpr: ["BranchCode", "BranchDescription"],
              searchTimeout: 10,
              searchEnabled: true,
              itemTemplate: (data) => {
                return ` <div class="custom-auto-complete">
                        <span class="auto-complete-primary">
                          ${data.BranchDescription}
                        </span>
                        <span class="record-secondary gc-grid-code">
                          ${data.BranchCode}
                        </span>
                      </div>`;
              },
              dropDownOptions: {
                width: 400,
              },
              onOpened: (e) => {
                if (e) e.component.getDataSource().reload();
              },
              onKeyDown: this.onBranchCodeKeyDown,
            },
            setCellValue: async (newData, value, currentRowData) => {
              newData.BranchCode = value;

              if (currentRowData.ProductCode && newData.BranchCode)
                await StocktakeCountService.getProductDefaultData(
                  this.baseView.id,
                  currentRowData.ProductCode,
                  newData.BranchCode
                )
                  .then((res) => {
                    if (res.data.value.length != 0) {
                      let item = res.data.value[0];
                      if (item) {
                        newData.UnitOfMeasureCode = item.UnitOfMeasureCode;
                        newData.ReferenceNo = item.ReferenceNo;
                        newData.BinLocationCode = item.BinLocationCode;
                        newData.BatchNo = item.BatchNo;
                        newData.SupplierCode = item.SupplierCode;
                        newData.Count = item.Count;
                        // newData.CountToDate = item.CountToDate;
                        newData.IsBatchProduct = item.IsBatchProduct;
                      }
                    }

                    return newData;
                  })
                  .catch(() => {
                    return newData;
                  });
              else return newData;
            },
          },
          {
            caption: "UM",
            dataField: "UnitOfMeasureCode",
            width: ColumnWidth.UnitOfMeasure.width,
            minWidth: ColumnWidth.UnitOfMeasure.minWidth,
            allowEditing: false,
          },
          {
            caption: "Ref No",
            dataField: "ReferenceNo",
            width: ColumnWidth.InvoiceRef.width,
            minWidth: ColumnWidth.InvoiceRef.minWidth,
            allowEditing: false,
          },
          {
            caption: "Bin",
            dataField: "BinLocationCode",
            width: ColumnWidth.InvoiceRef.width,
            minWidth: ColumnWidth.InvoiceRef.minWidth,
            cssClass: "edit-cell-textbox-field",
            editorOptions: {
              dataSource: ProductService.dataSourceConfig("api/StocktakeBin", {
                filter: [
                  ["StocktakeCode", "=", parseInt(this.baseView.id)],
                  "and",
                  ["BranchCode", "=", this.model.Branch],
                ],
                sort: "BinLocationLocation asc",
              }),
              displayExpr: "BinLocationCode",
              valueExpr: "BinLocationCode",
              searchExpr: ["BinLocationCode", "BinLocationLocation"],
              searchTimeout: 10,
              searchEnabled: true,
              itemTemplate: (data) => {
                return ` <div class="custom-auto-complete">
                        <span class="auto-complete-primary">
                          ${data.BinLocationLocation}
                        </span>
                        <span class="record-secondary gc-grid-code">
                          ${data.BinLocationCode}
                        </span>
                      </div>`;
              },
              dropDownOptions: {
                width: 400,
              },
              onKeyDown: this.onBinLocationCodeKeyDown,
            },
          },
          {
            caption: "Batch",
            dataField: "BatchNo",
            editCellTemplate: "batch-no-edit-template",
            cssClass: "edit-cell-textbox-field",
          },
          {
            caption: "Supplier",
            dataField: "SupplierCode",
            cssClass: "edit-cell-textbox-field",
            editCellTemplate: "supplier-cell-template",
          },
          {
            caption: "Count",
            dataField: "Count",
            width: ColumnWidth.Price2DecimalPoints.width,
            minWidth: ColumnWidth.Price2DecimalPoints.minWidth,
            format: "###,##0.0000",
            cellTemplate: "DecimalCell",
            cssClass: "edit-cell-numberbox-field",
            editorOptions: {
              format: "###,##0.0000",
              onKeyDown: this.onCountKeyDown,
            },
            setCellValue: (newData, value) => {
              newData.Count = value;

              return newData;
            },
          },
          {
            caption: "Count to Date",
            dataField: "CountToDate",
            allowEditing: false,
            width: ColumnWidth.Price2DecimalPoints.width,
            minWidth: ColumnWidth.Price2DecimalPoints.minWidth,
            format: "###,##0.0000",
            cellTemplate: "DecimalCell",
            cssClass: "edit-cell-numberbox-field",
            editorOptions: {
              format: "###,##0.0000",
              onKeyDown: (e, lineData) => {
                if (
                  e.event &&
                  lineData.rowIndex === this.countDataSource._array.length - 1
                ) {
                  if (forwardTabbing(e)) {
                    e.event.preventDefault();
                  }
                }
              },
            },
          },
          {
            caption: "",
            dataField: "",
            type: "buttons",
            width: 50,
            buttons: [
              {
                hint: "Add",
                name: "add",
                icon: "add",
                onClick: (e) => {
                  this.addNewRow(e);
                },
                visible: false, // Requ. changed to remove add line option
              },
              {
                hint: "Copy",
                icon: "copy",
                onClick: (e) => {
                  this.copyRowData(e);
                },
              },
              {
                hint: "Remove",
                name: "remove",
                icon: "trash",
                visible: (e) => {
                  let data;
                  if (e) {
                    if (e && e.row && e.row.data) {
                      data = e.row.data;
                      if (data.ReferenceNo) return false;
                      else if (data.AllowDelete === false) return false;
                    }
                  }
                  return true;
                },
                onClick: async (e) => {
                  let confirmDelete = await this.$confirm(
                    "Are you sure you want to delete this item? This action cannot be undone.",
                    "Delete Confirmation"
                  );

                  if (confirmDelete) {
                    if (this.dataGridViewType === "product-entry")
                      this.countDataSource.remove(e.row.data.ID).then(() => {
                        this.dataGrid.refresh(true);
                        this.countDataSource.load().then((items) => {
                          if (items.length === 0)
                            this.$nextTick(() => {
                              this.countDataSource.insert(itemBase);
                            });
                        });
                      });
                    else {
                      this.productListDataSource
                        .remove(e.row.data.ID)
                        .then(() => {
                          this.dataGrid.refresh(true);
                          this.productListDataSource.load().then((items) => {
                            if (items.length === 0)
                              this.$nextTick(() => {
                                this.productListDataSource.insert(itemBase);
                              });
                          });
                        });
                    }
                  }
                },
              },
              {
                hint: "Reversal",
                icon: "repeat",
                visible: (e) => {
                  let data;
                  if (
                    e &&
                    this.isEditable &&
                    this.getUserAccess
                  ) {
                    if (e && e.row && e.row.data) {
                      data = e.row.data;
                      if (data.ReferenceNo && data.Reverse != true) return true;
                    }
                  }
                  return false;
                },
                onClick: async (e) => {
                  let confirmReverse = await this.$confirm(
                    "Are you sure you want to reverse this item? This action cannot be undone.",
                    "Reversal Confirmation"
                  );

                  if (confirmReverse) this.reverseCountLine(e);
                },
              },
            ],
            visible: this.baseView.mode !== "review",
          },
        ];
    },
    getProductItemListColumns() {
      return [
        {
          caption: "Product",
          dataField: "ProductCode",
          width: ColumnWidth.ProductCode.width,
          allowEditing: false,
        },
        {
          caption: "Description",
          dataField: "ProductDescription",
          minWidth: ColumnWidth.ProductDescription.minWidth,
          allowEditing: false,
        },
        {
          caption: "UM",
          dataField: "UnitOfMeasureCode",
          width: ColumnWidth.UnitOfMeasure.width,
          minWidth: ColumnWidth.UnitOfMeasure.minWidth,
          allowEditing: false,
        },
        {
          caption: "Count",
          dataField: "Count",
          width: ColumnWidth.Price2DecimalPoints.width,
          minWidth: ColumnWidth.Price2DecimalPoints.minWidth,
          format: "###,##0.0000",
          cellTemplate: "DecimalCell",
          cssClass: "edit-cell-numberbox-field",
          editorOptions: {
            format: "###,##0.0000",
            onKeyDown: this.onItemCountKeyDown,
          },
          setCellValue: (newData, value) => {
            newData.Count = value;
            return newData;
          },
        },
        {
          caption: "",
          dataField: "CountStatus",
          width: 50,
          allowEditing: false,
          cellTemplate: "count-status-template",
        },
      ];
    },
    getDataSource() {
      if (this.dataGridViewType === "product-entry")
        return this.countDataSource;
      else if (this.dataGridViewType === "product-list") {
        return this.productListDataSource;
      } else return this.productListItemDataSource;
    },
    dataGrid() {
      const dataGrid = this.$refs["dx-data-grid-comp"];
      if (dataGrid) {
        return dataGrid.instance;
      }
      return undefined;
    },
    getFilterFormItems() {
      return [
        {
          screenField: "Name",
          filterLabel: "Product",
          filterDataField: "ProductSearchText",
          showInColumnChooser: false,
          visible: false,
          type: "Text",
          filterIndex: 1,
        },
        {
          screenField: "Supplier",
          gridDataField: "SupplierList",
          visible: false,
          showInColumnChooser: false,
          filterIndex: 2,
          filterDataField: "SupplierList",
          filterOperator: "contains",
        },
        {
          screenField: "ProductCategory1",
          label: "Category 1",
          filterLabel: "Category 1",
          filterIndex: 3,
        },
        {
          screenField: "ProductCategory2",
          label: "Category 2",
          filterLabel: "Category 2",
          filterIndex: 4,
        },
        {
          screenField: "ProductCategory3",
          label: "Category 3",
          filterLabel: "Category 3",
          filterIndex: 5,
        },
        {
          screenField: "ProductCategory4",
          label: "Category 4",
          filterLabel: "Category 4",
          filterIndex: 6,
        },
        {
          screenField: "ProductType",
          label: "Type",
          filterLabel: "Type",
          filterIndex: 7,
        },
        {
          screenField: "ProductSize",
          label: "Size",
          filterLabel: "Size",
          filterIndex: 8,
        },
        {
          screenField: "OrderStatus",
          filterLabel: "Count Status",
          gridDataField: "FriendlyStatus",
          filterDataField: "CountStatus",
          filterIndex: 9,
          dataSourceConfig: {
            data: ["Counted", "Pending"],
          },
          visible: this.dataGridViewType === "product-list-item",
        },
      ].concat(this.getAttributeColumns(10, false, false));
    },
    reviewFilter() {
      return [
        ["StocktakeCode", "=", parseInt(this.baseView.id)],
        "and",
        ["Status", "<>", "X"],
      ];
    },
    isEditable() {
      return this.model.Status != "C" && this.model.Status != "X";
    },
  },
  methods: {
    recallFromBatch(value) {
      if (value) {
        this.showLoading(true);
        this.getStocktakeReviewList();
      }
    },
    async getStocktakeReviewList() {
      this.productListItemDataSource.clear();
      this.productListDataSource.clear();
      await StocktakeCountService.getStocktakeReviewList({
        filter: `StocktakeCode eq ${parseInt(
          this.baseView.id
        )} and BranchCode eq '${this.model.Branch}' and Status ne 'X'`,
      })
        .then((res) => {
          this.showLoading(false);
          res.data.value.forEach((element) => {
            element.AllowDelete = false;
            element.ProductDescription = element.ProductDescription1;
            element.BatchNo = undefined;
            element.SupplierCode = undefined;
            const listItem = {
              ...element,
              ...{
                ReferenceNo:
                  element.CountStatus === "Counted" ? "ReferenceNo" : undefined,
              },
            };
            this.productListItemDataSource.insert(listItem);

            element.Count = 0;
            this.productListDataSource.insert(element);
          });

          if (this.dataGridViewType !== "product-entry") {
            this.$nextTick(() => {
              this.dataGrid.refresh(true);
            });
          }
        })
        .catch(() => {
          this.showLoading(false);
        });
    },
    async reverseCountLine(e) {
      this.showLoading(true);
      let lineData = { ...e.row.data };

      let payload = {
        ReferenceNo: "",
        BatchNo: lineData.BatchNo,
        BinLocationCode: lineData.BinLocationCode,
        BranchCode: lineData.BranchCode,
        Count:
          lineData.Count > 0
            ? -Math.abs(lineData.Count)
            : Math.abs(lineData.Count),
        ProductCode: lineData.ProductCode,
        StocktakeCode: this.baseView.id,
        SupplierCode: lineData.SupplierCode,
        UseByDate: lineData.UseByDate,
      };

      await StocktakeCountService.saveStocktakeCount(payload).then((result) => {
        if (result) {
          this.showLoading(false);
          this.updateProductCount(result);
        }
        if (lineData.ID) delete lineData.ID;

        this.changes = [
          {
            type: "insert",
            data: { ...lineData, ...result.data, ...{ Reverse: true } },
            insertAfterKey: e.row.key,
          },
        ];

        setTimeout(() => {
          this.dataGrid.saveEditData();
          this.dataGrid.refresh(true);
        }, 250);
      });
    },
    async copyRowData(e) {
      if (!e) return;
      if (!this.model.Branch) {
        await this.$alert(
          `The branch can not be empty. Please select a valid branch.`,
          "Warning"
        );

        this.$nextTick(() => {
          if (this.headerForm && this.headerForm.getEditor("Branch"))
            this.headerForm.getEditor("Branch").instance().focus();
        });
      } else {
        let lineData = { ...e.row.data };

        if (lineData.ID) delete lineData.ID;
        lineData.Count = 0;
        lineData.CountToDate = 0;
        lineData.ReferenceNo = undefined;
        lineData.rowUpdating = false;
        lineData.Reverse = false;
        lineData.AllowDelete = true;

        this.changes = [
          {
            type: "insert",
            data: { ...lineData },
            insertAfterKey: e.row.key,
          },
        ];

        setTimeout(() => {
          this.dataGrid.saveEditData();
          this.dataGrid.refresh(true);
        }, 250);
      }
    },
    displayBestOrUseDate(data) {
      if (data.BestBeforeDate) {
        return {
          text: "Best Before Date",
          value: new Date(data.BestBeforeDate).toShortDateString(),
        };
      } else if (data.UseByDate) {
        return {
          text: "Use By Date",
          value: new Date(data.UseByDate).toShortDateString(),
        };
      } else {
        return {
          text: "",
          value: "",
        };
      }
    },
    onCountKeyDown(e, lineData) {
      if (!e.event) return;

      const { rowIndex, data } = lineData;
      const isProductList = this.dataGridViewType === "product-list";
      const dataSource = isProductList
        ? this.productListDataSource
        : this.countDataSource;
      const isLastRow = rowIndex === dataSource._array.length - 1;

      if (backwardTabbing(e) && data.IsBatchProduct === "No") {
        e.event.preventDefault();
        this.focusOnEditableCell(rowIndex, "BinLocationCode");
        return;
      }

      if (forwardTabbing(e)) {
        if (isLastRow) {
          e.event.preventDefault();
        } else {
          let index = this.findNextNonEmptyReferenceNo(
            dataSource._array,
            rowIndex
          );
          if (index !== -1) {
            e.event.preventDefault();
            this.focusOnEditableCell(
              index,
              isProductList ? "BranchCode" : undefined
            );
          }
        }
      }
    },
    onItemCountKeyDown(e, lineData) {
      const visibleDataSet = this.dataGrid.getVisibleRows().map((x) => x.data);
      if (forwardTabbing(e)) {
        const { rowIndex } = lineData;
        const isLastRow = rowIndex === visibleDataSet.length - 1;
        if (isLastRow) {
          // e.event.preventDefault();
          setTimeout(() => {
            this.$nextTick(() => {
              this.dataGrid.option("focusedRowIndex", -1);
              // this.triggerLiveSave();
            });
          }, 200);
        } else {
          let index = this.findNextEditableItem(visibleDataSet, rowIndex);
          if (index !== -1) {
            e.event.preventDefault();
            this.focusOnEditableCell(index, "Count");
          }
        }
      }

      if (backwardTabbing(e)) {
        e.event.preventDefault();

        const index = this.productListItemDataSource._array.findIndex(
          (x) => x.ID === visibleDataSet[0].ID
        );
        if (index != -1 && index !== 0) {
          const scrollable = this.dataGrid.getScrollable();
          const currentPosition = scrollable.scrollTop(); // Get current scroll position

          const rowHeight = 40; // Approximate row height (adjust as needed)
          const rowsToScrollUp = 5; // Number of rows to scroll up

          scrollable.scrollTo({
            top: Math.max(currentPosition - rowHeight * rowsToScrollUp, 0),
          });
        }
        this.handleBackwardTabbing(e, { _array: visibleDataSet }, lineData);
      }
    },
    findNextEditableItem(array, currentIndex) {
      if (currentIndex >= array.length - 1) {
        return null; // There's no valid index beyond the last element
      }

      for (let i = currentIndex + 1; i < array.length; i++) {
        if (array[i].Count === 0) {
          return i; // Return the next index with a non-empty 'ReferenceNo'
        }
      }

      return null; // No matching index found
    },
    onBinLocationCodeKeyDown(e, lineData) {
      if (lineData.data.IsBatchProduct === "No" && e.event) {
        if (forwardTabbing(e)) {
          e.event.preventDefault();
          this.focusOnEditableCell(lineData.rowIndex, "Count");
        }
      }
    },
    handleBackwardTabbing(e, dataSource, lineData) {
      if (e.event && backwardTabbing(e)) {
        let index = this.findClosestEmptyReferenceNo(
          dataSource._array,
          this.dataGrid.getRowIndexByKey(lineData.data.ID)
        );
        e.event.preventDefault();

        if (index === null) {
          if (this.headerForm && this.headerForm.getEditor("Branch")) {
            let branchEditor = this.headerForm.getEditor("Branch").instance();
            if (branchEditor) {
              branchEditor.focus();
            }
          }
        } else {
          this.focusOnEditableCell(index, "Count");
        }
      }
    },
    onProductCodeKeyDown(e, lineData) {
      this.handleBackwardTabbing(e, this.countDataSource, lineData);
    },
    onBranchCodeKeyDown(e, lineData) {
      if (this.dataGridViewType === "product-list") {
        this.handleBackwardTabbing(e, this.productListDataSource, lineData);
      }
    },
    findClosestEmptyReferenceNo(array, currentIndex) {
      if (currentIndex <= 0) {
        return null; // There's no valid index before index 0
      }

      for (let i = currentIndex - 1; i >= 0; i--) {
        if (
          this.dataGridViewType === "product-list-item" &&
          array[i].Count === 0
        )
          return i; // this.dataGrid.getRowIndexByKey(array[i].ID);

        if (!array[i].ReferenceNo) {
          return i; // Return the closest index with an empty 'ReferenceNo'
        }
      }

      return null; // No matching index found
    },
    findNextNonEmptyReferenceNo(array, currentIndex) {
      if (currentIndex >= array.length - 1) {
        return null; // There's no valid index beyond the last element
      }

      for (let i = currentIndex + 1; i < array.length; i++) {
        if (!array[i].ReferenceNo) {
          return i; // Return the next index with a non-empty 'ReferenceNo'
        }
      }

      return null; // No matching index found
    },
    backButtonFunction() {
      this.$router
        .push({
          path: this.backButtonRoute,
        })
        .then(() => {
          this.$store.dispatch("dataContext/setDataViewType", undefined);
        });
    },
    onHeaderFormContentReady(e) {
      const headerForm = document.querySelectorAll(".gc-header-form");
      this.headerFormHeight = headerForm[0].clientHeight;

      if (e && this.baseView.mode != "review" && !this.onFocusIn) {
        this.onFocusIn = true;
        setTimeout(() => {
          this.headerForm = e.component;
          if (this.headerForm && this.headerForm.getEditor("Branch"))
            this.headerForm.getEditor("Branch").instance().focus();
        }, 500);
      }
    },
    onBranchValueChanged(e) {
      if (e.event && e.value) {
        this.model.Branch = e.value;
        this.showLoading(true);
        this.getStocktakeReviewList();
        // this.countDataSource._array.forEach((element) => {
        //   if (element.ProductCode)
        //     this.countDataSource.update(element.ID, {
        //       BranchCode: e.value,
        //     });
        // });
      }
    },
    onBranchKeyDown(e) {
      if (onEnter(e) || forwardTabbing(e)) {
        e.event.preventDefault();

        let column = "ProductCode";
        let item = this.countDataSource._array.find((x) => !x.ReferenceNo);
        if (this.dataGridViewType === "product-list") {
          column = "BranchCode";
          item = this.productListDataSource._array.find((x) => !x.ReferenceNo);
        } else if (this.dataGridViewType === "product-list-item") {
          column = "Count";
          item = this.productListItemDataSource._array.find(
            (x) => x.Count === 0
          );
        }
        if (item) {
          let index = this.dataGrid.getRowIndexByKey(item.ID);
          if (index != -1) {
            this.focusOnEditableCell(index, column);
          }
        }
      } else if (backwardTabbing(e)) {
        e.event.preventDefault();
      }
    },
    onEditorPreparing(e) {
      if (
        e.dataField &&
        ((e.dataField === "ProductCode" &&
          this.dataGridViewType === "product-entry") ||
          e.dataField === "BranchCode" ||
          e.dataField === "BinLocationCode")
      ) {
        e.editorName = "dxSelectBox";
      }

      if (e.editorOptions && e.editorOptions.onKeyDown) {
        let func = e.editorOptions.onKeyDown;
        if (this.baseView.mode !== "review" && e.parentType !== "searchPanel")
          e.editorOptions.onKeyDown = (event) => {
            func(event, e.row);
          };
      }
    },
    onEditingStart(e) {
      if (e.data) {
        if (this.dataGridViewType === "product-list-item") {
          if (
            e &&
            e.column &&
            e.column.dataField === "Count" &&
            (e.data.CountToDate != 0 || e.data.CountStatus === "Counted")
          ) {
            e.cancel = true;
          }
        }
        if (e.data.ReferenceNo) {
          e.cancel = true;
        }
      }

      if (e && e.column && e.column.dataField === "ProductCode") {
        if (this.dataGridViewType !== "product-entry") e.cancel = true;
      }
    },
    async addNewRow(e) {
      if (!this.model.Branch) {
        await this.$alert(
          `The branch can not be empty. Please select a valid branch.`,
          "Warning"
        );

        this.$nextTick(() => {
          if (this.headerForm && this.headerForm.getEditor("Branch"))
            this.headerForm.getEditor("Branch").instance().focus();
        });
      } else {
        this.changes = [
          {
            type: "insert",
            data: itemBase,
            insertAfterKey: e.row.key,
          },
        ];

        setTimeout(() => {
          this.dataGrid.saveEditData();
          this.dataGrid.refresh(true);
        }, 250);
      }
    },
    onSaved(e) {
      if (e.changes && e.changes.length > 0) {
        const change = e.changes[0];
        if (change.insertAfterKey !== undefined) {
          const element = change.data;
          let column = undefined;
          let index = -1;
          if (this.dataGridViewType === "product-entry") {
            index = this.countDataSource._array.findIndex(
              (x) => x.ID === change.insertAfterKey
            );
            this.countDataSource._array.splice(
              this.countDataSource._array.length - 1,
              1
            );
            this.countDataSource._array.splice(index + 1, 0, element);
          } else if (this.dataGridViewType === "product-list") {
            column = "BranchCode";
            index = this.productListDataSource._array.findIndex(
              (x) => x.ID === change.insertAfterKey
            );
            this.productListDataSource._array.splice(
              this.productListDataSource._array.length - 1,
              1
            );
            this.productListDataSource._array.splice(index + 1, 0, element);
          }

          if (index !== -1 && element.Reverse !== true)
            setTimeout(() => {
              this.focusOnEditableCell(index + 1, column);
            }, 100);
        }
      }
    },
    /**
     *
     * @param rowIndex
     * @param column
     */
    focusOnEditableCell(rowIndex, column = "ProductCode") {
      const dataGrid = this.dataGrid;

      const cellElement = dataGrid.getCellElement(rowIndex, column);
      dataGrid.focus(cellElement);

      this.$nextTick(() => {
        dataGrid.editCell(rowIndex, column);
      });
    },
    getBatchNoDataSource(ProductCode) {
      return StocktakeCountService.dataSourceConfig("api/StockBatch", {
        select: [
          "BatchNo,UseByDate,SupplierName,SupplierCode,BatchDateCreated,StocktakeCode,BranchCode,ProductCode,BestBeforeDate",
          "RecordId",
        ],
        sort: [{ selector: "BatchDateCreated", desc: true }],
        filter: [
          ["StocktakeCode", "=", parseInt(this.baseView.id)],
          "and",
          ["BranchCode", "=", this.model.Branch],
          "and",
          ["ProductCode", "=", ProductCode],
          "and",
          ["BatchNo", "<>", ""],
        ],
        key: ["RecordId"],
        keyType: {
          RecordId: "Guid",
        },
        // postProcess: (data) => {
        //   var items = [];
        //   data.forEach((element) => {
        //     if (element.BatchNo != "") {
        //       items.push(element);
        //     }
        //   });
        //   return items;
        // },
      });
    },
    getSupplierDataSource(ProductCode) {
      return StocktakeCountService.dataSourceConfig("api/ProductSupplierList", {
        select: ["SupplierCode,SupplierName,ProductCode"],
        sort: [{ selector: "SupplierCode", asc: true }],
        filter: ["ProductCode", "=", ProductCode],
      });
    },
    async onProductCodeFocusIn() {
      if (!this.model.Branch) {
        await this.$alert(
          `The branch can not be empty. Please select a valid branch.`,
          "Warning"
        );

        this.$nextTick(() => {
          if (this.headerForm && this.headerForm.getEditor("Branch"))
            this.headerForm.getEditor("Branch").instance().focus();
        });
      }
    },
    onProductSelectionChanged(itemData) {
      if (itemData) {
        let index = this.selectedProducts.findIndex(
          (x) => x.ProductCode === itemData.ProductCode
        );
        if (index === -1) this.selectedProducts.push(itemData);
      }
    },

    async onFocusedRowChanged(e) {
      if (e) {
        setTimeout(() => {
          this.$nextTick(() => {
            this.triggerLiveSave();
          });
        }, 200);
      }
    },
    async triggerLiveSave() {
      let lineData = undefined;
      if (this.dataGridViewType === "product-entry")
        lineData = this.countDataSource._array.find((x) => x.rowUpdating);
      else if (this.dataGridViewType === "product-list") {
        lineData = this.productListDataSource._array.find((x) => x.rowUpdating);
      } else {
        lineData = this.productListItemDataSource._array.find(
          (x) => x.rowUpdating
        );
      }

      let focusedRowIndex = this.dataGrid.option("focusedRowIndex");

      let allowSave = true;
      if (lineData && lineData.Count === 0) allowSave = false;
      else if (
        lineData &&
        lineData.IsBatchProduct != "No" &&
        this.dataGridViewType !== "product-list-item"
      ) {
        if (!lineData.BatchNo || !lineData.SupplierCode) allowSave = false;
      }

      if (lineData && allowSave) {
        let rowIndex = this.dataGrid.getRowIndexByKey(lineData.ID);
        if (focusedRowIndex === rowIndex) return;
        if (
          lineData.IsBatchProduct != "No" &&
          !lineData.BatchNo &&
          this.dataGridViewType !== "product-list-item"
        ) {
          await this.$alert(`Batch No is required`, "Warning");
          await this.focusOnEditableCell(rowIndex, "BatchNo");
          return;
        }

        let payload = {
          ReferenceNo: lineData.ReferenceNo ? lineData.ReferenceNo : "",
          BatchNo: lineData.BatchNo,
          BinLocationCode: lineData.BinLocationCode,
          BranchCode: lineData.BranchCode,
          Count: lineData.Count,
          ProductCode: lineData.ProductCode,
          StocktakeCode: this.baseView.id,
          SupplierCode: lineData.SupplierCode,
          UseByDate: lineData.UseByDate,
        };
        await this.saveStocktakeCount(payload, lineData.ID);
        this.$nextTick(() => {
          this.dataGrid.refresh(true);
        });
      } else {
        if (lineData) {
          this.updateLocalDataSet(lineData.ID, {
            rowUpdating: false,
          });
        }
      }
    },
    onBatchNoSelectionChanged(e, lineData) {
      let itemData = e.itemData;
      if (itemData) {
        e.component.beginUpdate();
        e.component.option("value", itemData.RecordId);
        e.component.endUpdate();
        this.updateLocalDataSet(lineData.ID, {
          BatchNo: itemData.BatchNo,
          SupplierCode: itemData.SupplierCode,
          rowUpdating: true,
          IsCustomBatch: itemData.IsCustomBatch ? true : false,
        });
      }
    },
    onSupplierCodeSelectionChanged(e, lineData) {
      let itemData = e.selectedItem;
      if (itemData)
        this.updateLocalDataSet(lineData.ID, {
          BatchNo:
            lineData.SupplierCode !== itemData.SupplierCode &&
            lineData.IsCustomBatch != true
              ? ""
              : lineData.BatchNo,
          SupplierCode: itemData.SupplierCode,
          SupplierName: itemData.SupplierName,
          rowUpdating: true,
        });
    },
    onBatchCustomItemCreating(e, data, fieldName) {
      if (!e.text) {
        e.customItem = null;
        return;
      }

      const newItem = {
        BatchNo: e.text,
        SupplierCode: undefined,
        IsCustomBatch: true,
      };
      data.data[fieldName] = e.text;

      e.customItem = newItem;
    },

    async saveStocktakeCount(payload, ID) {
      this.showLoading(true);
      await StocktakeCountService.saveStocktakeCount(payload)
        .then((result) => {
          this.showLoading(false);
          this.updateLocalDataSet(ID, {
            ReferenceNo: result.data.ReferenceNo,
            CountToDate: result.data.CountToDate,
            rowUpdating: false,
            CountStatus: result.data.CountStatus,
          });

          this.updateProductCount(result);
        })
        .catch(async (err) => {
          this.showLoading(false);
          // this.displayServerError(err, "Stocktake Count Commit Failed");
          if (
            err.data.ValidationErrors &&
            err.data.ValidationErrors.length > 0
          ) {
            const ex = await this.$alert(
              err.data.ValidationErrors[0].Message,
              "Server Error"
            );
            if (ex) {
              this.$nextTick(() => {
                if (err.data.ValidationErrors[0].RefreshData)
                  this.recallFromBatch(true);
              });
            }
          } else this.displayServerError(err);
        });
    },

    updateProductCount(result) {
      const dataItems =
        this.dataGridViewType === "product-entry"
          ? this.countDataSource._array
          : this.productListDataSource._array;

      const { ProductCode, BranchCode, BatchNo, CountToDate } = result.data;

      const matchingIDs = dataItems
        .filter(
          (item) =>
            item.ProductCode === ProductCode &&
            item.BranchCode === BranchCode &&
            (!BatchNo || item.BatchNo === BatchNo) // Skip BatchNo check if it's falsy
        )
        .map((item) => item.ID);

      const dataSource =
        this.dataGridViewType === "product-entry"
          ? this.countDataSource
          : this.productListDataSource;

      matchingIDs.forEach((id) => {
        dataSource.update(id, { CountToDate });
      });
    },
    rowUpdated(e) {
      let data = e.data;
      this.updateLocalDataSet(data.ID, {
        rowUpdating: true,
      });
    },
    async cancelStockTake() {
      let confirmCancel = await this.$confirm(
        "Are you sure you want to cancel this stocktake ?",
        "Cancel Stocktake"
      );

      if (confirmCancel) {
        let payload = { Code: this.baseView.id };
        this.showLoading(true);
        await StockTakeListService.cancelStockTake(payload)
          .then(() => {
            this.showLoading(false);
            this.setIgnoreRouteChangeConfirmation();
            this.$router.push({
              path: "/stocktake",
            });
          })
          .catch((err) => {
            this.showLoading(false);
            this.displayServerError(err, "Cancel Stocktake line Failed");
          });
      }
    },

    async commitStockTake() {
      this.showLoading(true);
      let payload = { Code: this.baseView.id };

      await StockTakeListService.commitStockTake(payload)
        .then(() => {
          this.showLoading(false);

          this.$notify(
            `Stocktake ${this.baseView.id} Successfully Commited`,
            "success"
          );
          this.setIgnoreRouteChangeConfirmation();
          this.$router.push({
            path: "/stocktake",
          });
        })
        .catch((err) => {
          this.showLoading(false);
          this.displayServerError(err, "Stocktake Commit Failed");
        });
    },

    onToolbarPreparing(e) {
      var toolbarItems = e.toolbarOptions.items;
      var searchPanel = toolbarItems.find((item) => {
        return item.name === "searchPanel";
      });
      let itemsToRemoveFromToolBar = ["searchPanel"];

      e.toolbarOptions.items = toolbarItems.filter(
        (item) => !itemsToRemoveFromToolBar.includes(item.name)
      );
      searchPanel.location = "after";

      e.toolbarOptions.items.unshift(
        {
          location: "before",
          widget: "dxSelectBox",
          options: {
            width: 200,
            items: this.StockTakeGridViews,
            displayExpr: "label",
            valueExpr: "value",
            value: this.dataGridViewType,
            onValueChanged: (e) => {
              this.dataGridViewType = e.value;

              setTimeout(() => {
                if (this.showRightContainer) {
                  var item = document.getElementById("filter-btn");
                  item.classList.add("dx-state-invisible");
                }
                // this.reSizeGrid();

                this.$nextTick(() => {
                  if (this.dataGridViewType !== "product-entry") {
                    this.showLoading(true);
                    this.getStocktakeReviewList();
                  }
                  this.updateFilterFormItems();
                });
              }, 100);
            },
          },
        },
        searchPanel,
        {
          location: "after",
          locateinmenu: "auto",
          widget: "dxButton",
          options: {
            tabIndex: -1,
            validationGroup: "formsData",
            icon: "filter",
            elementAttr: { id: "filter-btn" },
            onClick: () => {
              this.showRightContainer = !this.showRightContainer;
              setTimeout(() => {
                var item = document.getElementById("filter-btn");
                item.classList.add("dx-state-invisible");
                // this.reSizeGrid();
              }, 100);
            },
          },
        }
      );
    },

    updateLocalDataSet(ID, updateData) {
      if (this.dataGridViewType === "product-entry")
        return this.countDataSource.update(ID, updateData);
      else if (this.dataGridViewType === "product-list") {
        return this.productListDataSource.update(ID, updateData);
      } else {
        return this.productListItemDataSource.update(ID, updateData);
      }
    },

    closeFilterPanel() {
      this.showRightContainer = !this.showRightContainer;

      var item = document.getElementById("filter-btn");
      item.classList.remove("dx-state-invisible");

      this.$nextTick(() => {
        this.reSizeGrid();
      });
    },
    reSizeGrid() {
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;

      dataGrid.beginUpdate();
      dataGrid.updateDimensions();
      dataGrid.endUpdate();
    },

    // Filter
    setGridPanelFilter(e, valueChanged) {
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      // valueChanged === true means filter is applied.
      if (!valueChanged) return;
      else {
        this.filterApplied = true;
        if (this.baseView.mode === "create") {
          dataGrid.clearSelection();
          if (e.length === 0) this.filterArray = false;
          else {
            this.filterArray = undefined;
            let filter = [...e];
            filter.push("and");
            filter.push([["Inactive", "=", "N"]]);
            this.filterArray = [...filter];
          }

          dataGrid.refresh();
        } else {
          if (e.length > 0) {
            let filter = [];
            filter.push([...this.reviewFilter]);
            filter.push("and");
            filter.push([...e]);

            dataGrid.filter(filter);
          } else {
            dataGrid.filter([...this.reviewFilter]);
          }
        }
      }
    },

    updateFilterFormItems() {
      const filterForm =
        this.$refs["gc-filter-panel"].$refs["gc-form"].$refs["gc-form-comp"]
          .instance;
      if (filterForm) {
        const items = filterForm.option().items;
        items[0].items.forEach((element) => {
          if (element.dataField === "CountStatus") {
            if (this.dataGridViewType === "product-list-item")
              element.visible = true;
            else element.visible = false;
          }
        });
        filterForm.option("items", items);
        filterForm.repaint();
      }
    },

    // Batch View
    clickOnBatchView(data) {
      this.stockTakeLineData = data;
      this.visibleBatchCount = true;
    },
  },
};
</script>

<style lang="scss">
.stock-take-count {
  .dx-datagrid .dx-datagrid-content .dx-datagrid-table .dx-row .dx-editor-cell {
    padding-left: 5px !important;
    padding-right: 5px !important;
  }

  .gc-batch-selection {
    .dx-placeholder::before {
      padding-right: unset !important;
      padding-left: unset !important;
      font-size: 11px;
    }
  }

  .gc-header-form {
    > div.view-card.responsive-paddings {
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      > div:first-child {
        width: 100%;
      }

      > div:last-child {
        display: flex;
        justify-content: space-between;
        align-items: center;

        > div:not(:last-child) {
          margin-right: 10px;
        }
      }
    }
  }

  .dx-command-edit.dx-command-edit-with-icons {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: row-reverse;
  }

  .count-status-template {
    text-align: center;

    .on-pending-state {
      cursor: pointer;
    }
  }
}

.batch-no-edit-template {
  display: inline-flex;
  justify-content: space-between;
}
.batch-no-edit-template::before {
  content: unset !important;
}
</style>
